import { Editor, Transforms, Point, Node } from 'slate';
import { times } from 'lodash';

// Create table's cell
const createCell = () => ({
	type: 'table-cell',
	children: [{ text: '' }]
});

// Create table's row with cells
const createRow = columns => ({
	type: 'table-row',
	children: times(columns, () => createCell())
});

// Create table with rows
const createTable = (rows, columns) => ({
	type: 'table',
	children: times(rows, () => createRow(columns))
});

const withTables = editor => {
	const {
		deleteFragment,
		deleteBackward,
		deleteForward,
		insertBreak
	} = editor;

	// Check if selection is in table
	editor.isInTable = () => {
		const [table] = Editor.nodes(editor, {
			match: node => node.type === 'table'
		});

		return !!table;
	};

	// Moves selection backwards on delete
	editor.moveTableSelectionBackwards = () => {
		const [, previousCellPath] = editor.getPreviousElement('table-cell') ?? [];

		if (!previousCellPath) {
			const [, previousRowPath] = editor.getPreviousElement('table-row') ?? [];

			if (previousRowPath) {
				// Get previous row's last cell's path
				const [, lastPath] = Editor.last(editor, previousRowPath);

				// Move anchor to previous row's last cell's endpoint
				Transforms.select(editor,
					Editor.end(editor, lastPath)
				);
			}
		} else {
			// Move anchor to previous cell's endpoint
			Transforms.select(editor,
				Editor.end(editor, previousCellPath)
			);
		}
	};

	// Moves selection forwards on tab
	editor.moveTableSelectionForwards = () => {
		const [, nextCellPath] = editor.getNextElement('table-cell') ?? [];

		if (!nextCellPath) {
			const [, nextRowPath] = editor.getNextElement('table-row') ?? [];

			if (!nextRowPath) {
				editor.insertTableRow();
			} else {
				const [, firstPath] = Editor.first(editor, nextRowPath);

				Transforms.select(editor,
					Editor.end(editor, firstPath)
				);
			}
		} else {
			Transforms.select(editor,
				Editor.end(editor, nextCellPath)
			);
		}
	};

	// Handle's backspace deletion event
	// @args: default arguments
	editor.deleteBackward = (args) => {
		// Get current offset
		const offset = editor?.selection?.anchor?.offset;

		// Delete normaly if not at block's begining
		if (offset > 0) return deleteBackward(args);

		// Move selection if empty cell
		if (editor.isInTable()) {
			return editor.moveTableSelectionBackwards();
		}

		// Disable deletion if previous element is table
		if (!editor.isInTable()) {
			// Get previous element's path
			const [, path] = Editor.previous(editor, {
				mode: 'lowest'
			}) ?? [];

			// Get previous table
			const [table] = Editor.above(editor, {
				at: path,
				match: node => node.type === 'table'
			}) ?? [];

			// Delete normaly if table is not above
			if (!table) return deleteBackward(args);
		}
	};

	// Handle's delete deletion event
	// @args: default arguments
	editor.deleteForward = (args) => {
		// Get current path
		const currentPath = editor?.selection?.anchor;

		// Get current element's path
		const [, elementPath] = Editor.above(editor, {
			mode: 'lowest'
		}) || [];

		// Get current element's endpoint
		const endPath = Editor.end(editor, elementPath);

		// Delete normaly if not at the end
		if (!Point.equals(currentPath, endPath)) {
			return deleteForward(args);
		}

		// Disable deletion if next element is table
		if (!editor.isInTable()) {
			// Get next element's path
			const [, path] = Editor.next(editor, {
				mode: 'lowest'
			}) ?? [];

			// Get next table
			const [element] = Editor.above(editor, {
				at: path,
				match: node => node.type === 'table'
			}) ?? [];

			// Delete normaly if table is not above
			if (!element) return deleteForward(args);
		}
	};

	editor.deleteFragment = () => {
		// Proceed with default handler if not in table
		if (!editor.isInTable()) return deleteFragment();

		// Remove whole row in selection
		Transforms.removeNodes(editor, {
			match: node => node.type === 'table-row'
		});

		// Continue with default fragment deletion
		deleteFragment();
	};

	// Handle's break event inside table
	// @args: default arguments
	editor.insertBreak = (args) => {
		// Run default delete fuction if not in table
		if (!editor.isInTable()) return insertBreak(args);

		// In tables on enter insert only a line break
		Transforms.insertText(editor, '\n');
	};

	// Insert table in current selection
	// @rows: number | table's row count
	// @columns: number | table's row column count
	editor.insertTable = (rows = 2, columns = 2) => {
		// Do not create table in table
		if (editor.isInTable()) return;

		// Create table markup with given rows and columns
		const table = createTable(rows, columns);

		// Find current element
		const [current] = Editor.above(editor) ?? [];

		// Find next element
		const next = Editor.next(editor);

		// Remove empty node
		if (current && Node.string(current) === '') {
			Transforms.removeNodes(editor);
		}

		// Insert table in current selection
		Transforms.insertNodes(editor, table);

		// Insert empty paragraph node if there is no next node
		if (!next) {
			Transforms.insertNodes(editor, {
				type: 'paragraph',
				children: [{ text: '' }]
			}, {
				at: [editor?.children?.length]
			});
		}
	};

	// Insert row after current selection
	editor.insertTableRow = () => {
		const [row, path] = editor.getCurrentElement('table-row');
		const [tablePath, rowPath] = path;
		const cols = row.children.length;

		// Insert new row and columns after current row
		Transforms.insertNodes(editor, createRow(cols), {
			at: [tablePath, rowPath + 1]
		});

		// Find inserted rows path
		const [, insertedRowPath] = Editor.first(editor,
			[tablePath, rowPath + 1]
		);

		// Move anchor to inserted row's first cell
		Transforms.select(editor, insertedRowPath);
	};

	// Insert column after current selection
	editor.insertTableColumn = () => {
		const [table] = editor.getCurrentElement('table');
		const [, path] = editor.getCurrentElement('table-cell');
		const [tablePath, rowPath, columnPath] = path;
		const rows = table.children.length;

		// Insert new cell after current cell in each row
		for (let r = 0; r < rows; r++) {
			Transforms.insertNodes(editor, createCell(), {
				at: [tablePath, r, columnPath + 1]
			});
		}

		// Find inserted rows path
		const [, insertedColumnPath] = Editor.first(editor,
			[tablePath, rowPath, columnPath + 1]
		);

		// Move anchor to insered cell in the same row
		Transforms.select(editor, insertedColumnPath);
	};

	// Removes table with rows and columns
	editor.deleteTable = () => {
		// Only run if selection is in table
		if (!editor.isInTable()) return;

		// Remove table with all rows and columns
		Transforms.removeNodes(editor, {
			match: node => node.type === 'table'
		});
	};

	// Delete row in current selection
	editor.deleteTableRow = () => {
		const [table, tablePath] = editor.getCurrentElement('table');
		const [, path] = editor.getCurrentElement('table-row');

		// Remove table if only one row
		if (table.children.length === 1) {
			return Transforms.removeNodes(editor, { at: tablePath });
		}

		// Remove table row
		Transforms.removeNodes(editor, { at: path });
	};

	// Delete column in current selection
	editor.deleteTableColumn = () => {
		const [table, tablePath] = editor.getCurrentElement('table');
		const [row] = editor.getCurrentElement('table-row');
		const [, path] = editor.getCurrentElement('table-cell');

		const rows = table.children.length;
		const cols = row.children.length;
		const [,, columnPath] = path;

		// Remove table if only one cell
		if (cols === 1) {
			return Transforms.removeNodes(editor, { at: tablePath });
		}

		// Remove current cell in every row
		for (let r = 0; r < rows; r++) {
			Transforms.removeNodes(editor, {
				at: [tablePath, r, columnPath]
			});
		}
	};

	return editor;
};

export default withTables;
